import { Inject, Injectable, PLATFORM_ID } from '@angular/core';
import { DOCUMENT, isPlatformBrowser } from '@angular/common';
import {
	MissingTranslationHandler,
	MissingTranslationHandlerParams,
	TranslateService as NGXTranslateService,
} from '@ngx-translate/core';
import { Observable, of } from 'rxjs';
import { ILang } from './translates.interface';
import { StorageService } from 'src/app/core/services/storage.service';
import { StorageEnums } from '../models/enums';
import { MyLogger } from '../services/helpers/logging.service';

const LANG_LIST: ILang[] = [
	{ code: 'en', name: 'English', culture: 'en-EN' },
	{ code: 'de', name: 'German', culture: 'de-GE' },
];
const LANG_DEFAULT: ILang = LANG_LIST[0];
let isUserLogin: boolean;
let LANG_FROM_DEVICE: string;
let LANG_FROM_STORAGE: string;

@Injectable()
export class TranslatesService extends MyLogger {
	constructor(
		@Inject(PLATFORM_ID) private _platformId: Object,
		@Inject(DOCUMENT) private _document: any,
		@Inject(NGXTranslateService) private _translate: NGXTranslateService,
		private _appStorage: StorageService
	) {
		super('TranslatesService');
	}

	public async initLanguage(): Promise<void> {
        try {
            const [deviceLang, storedLang, loggedIn] = await Promise.all([
                this.getPreferredLanguage(),
                this._appStorage.getItem(StorageEnums.LANG),
                this._appStorage.getItem(StorageEnums.USER_LOGGED_IN)
            ]);
    
            LANG_FROM_DEVICE = deviceLang;
            LANG_FROM_STORAGE = storedLang;
            isUserLogin = !!loggedIn;
        } catch (e) {
            console.warn('Language detection fallback:', e);
        }
    
        const lang = this._getLanguage();
    
        this._translate.addLangs(LANG_LIST.map((l: ILang) => l.code));
    
        this._translate.setDefaultLang(LANG_DEFAULT.code);
        await this._setLanguage(lang);
    }    
    
    private async getPreferredLanguage(): Promise<string> {
        if (!isPlatformBrowser(this._platformId)) {
            return LANG_DEFAULT.code;
        }
    
        // Prefer navigator.language or navigator.languages
        const nav = window.navigator as any;
    
        let lang: string | undefined;
    
        if (Array.isArray(nav.languages) && nav.languages.length > 0) {
            lang = nav.languages[0];
        } else if (nav.language) {
            lang = nav.language;
        }
    
        // Normalize like "en-US" → "en"
        return lang ? lang.slice(0, 2).toLowerCase() : LANG_DEFAULT.code;
    }    

	private _getLanguage(): ILang {
        let language: string | undefined;
    
        if (LANG_FROM_STORAGE && isUserLogin) {
            language = LANG_FROM_STORAGE;
        } else if (LANG_FROM_DEVICE) {
            language = LANG_FROM_DEVICE;
        } else if (isPlatformBrowser(this._platformId)) {
            language = this._translate.getBrowserLang() || LANG_DEFAULT.code;
        }
        
        return this._getFindLang(language);
    }    

    private _getFindLang(code: string): ILang {
        const shortCode = code?.slice(0, 2);
        const lang = LANG_LIST.find((l: ILang) => l.code === shortCode);
        return lang ? lang : LANG_DEFAULT;
    }

	private async _setLanguage(lang: ILang) {
		console.log('set lang', lang);
		this._translate.use(lang.code).subscribe(() => {
			this._document.documentElement.lang = lang.code;
		});
		await this._appStorage.setItem(StorageEnums.LANG, lang.code);
	}

	public changeLang(code: string): void {
		const lang: ILang = this._getFindLang(code);
		if (!lang || lang.code === this._translate.currentLang) {
			return;
		}
		this._setLanguage(lang);
	}

	public getLangList(): Observable<ILang[]> {
		return of(LANG_LIST);
	}

	public getCurrentLang(): string {
		return this._translate.currentLang;
	}
}

export class CommonMissingTranslationHandler implements MissingTranslationHandler {
	handle(params: MissingTranslationHandlerParams) {
		const lang = params.translateService.currentLang;
		const translations = params.translateService.translations[lang];

		if (
			params.key.includes('.') &&
			translations &&
			!translations[params.key]
		) {
			console.warn(`[i18n] Missing translation for [${lang}]: "${params.key}"`);
		}

		return params.key;
	}
}